import { FC, PropsWithChildren, useEffect, useState } from "react";
import { OpenFeature, ProviderEvents } from "@openfeature/web-sdk";
import { Loader } from "@/components/Loader/Loader";

/**
 * Maximum wait time for the OpenFeature provider to be ready
 * before proceeding with local featureflags.
 */
const MAX_WAIT_TIME_MS = 3000;

/**
 * FeatureFlagGate component ensures that the OpenFeature client is ready
 * before rendering the children. Making sure that feature flags are available.
 */
export const FeatureFlagGate: FC<PropsWithChildren> = ({ children }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const client = OpenFeature.getClient();
    const timeout = setTimeout(() => {
      // Safety net: do not block forever
      setReady(true);
    }, MAX_WAIT_TIME_MS);

    const onReady = () => {
      clearTimeout(timeout);
      setReady(true);
    };

    const onError = () => {
      clearTimeout(timeout);
      setReady(true);
    };

    client.addHandler(ProviderEvents.Ready, onReady);
    client.addHandler(ProviderEvents.Error, onError);

    return () => {
      clearTimeout(timeout);
      client.removeHandler(ProviderEvents.Ready, onReady);
      client.removeHandler(ProviderEvents.Error, onError);
    };
  }, []);

  if (!ready) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader size="md" />
      </div>
    );
  }

  return <>{children}</>;
};
